import _filter from "lodash/filter";
import _find from "lodash/find";
import _forEach from "lodash/forEach";
import _get from "lodash/get";
import { FORM_ID, MaybeEmpty } from "../../../../constants";
import { RootState, Form } from "../../../../models";
import { VALIDATION_FIELD } from "../../rapidform/formValidation";
import { DiyPackageTypes, diyPackageTypes } from "../../../../models/common";
import { Package, FwaState } from "../../../../models/state";
import { getFormattedPrice } from "../../../../shared/priceFormatter";
import { SMART_WIFI_ROUTER_PRODUCT_IDS } from "../constants";
import * as properNameCase from "../../../../shared/properNameCase";

export interface Item {
    key: string;
    strikethrough?: string;
    value: string;
}

export const getFwaData = (state: RootState): MaybeEmpty<FwaState> => _get(state, "fwa", {});

// @ts-expect-error: form should exist
export const getFormById = (state: RootState, id: typeof FORM_ID.PERSONAL_DETAILS_FWA): Form => getFwaData(state)[id];

// Address
export const getFwaAddress = (state: RootState) => getFwaData(state).address;
export const getFwaAddressFields = (state: RootState) => getFwaAddress(state)?.fields;
export const getStreetNames = (state: RootState) => getFwaAddress(state)?.streetNames || [];
export const getStreetNumbers = (state: RootState) => getFwaAddress(state)?.streetNumbers || [];

export const getSelected = (state: RootState) => getFwaAddress(state)?.selected;
export const getSelectedAddress = (state: RootState, omitZipCode?: boolean) => {
    const address = getSelected(state);
    const street = properNameCase.convert(`${address?.streetName || ""} ${address?.streetNumber || ""}`.trim());
    const zipCode = omitZipCode ? "" : address?.zipCode || "";
    const city = properNameCase.convert(address?.city) || "";
    return `${street ? street + ", " : ""}${zipCode} ${city}`;
};
export const getVAID = (state: RootState) => getSelected(state)?.vaid;
export const successfullySetVaid = (state: RootState) => !!getFwaAddress(state)?.setVaidSuccess;
export const isCustomAddressSelected = (state: RootState) => getFwaAddress(state)?.isCustomAddressSelected || false;
export const getBackUrl = (state: RootState) => getFwaAddress(state)?.backUrl;

// Packages
export const getPackages = (state: RootState) => getFwaData(state).packages;

const getAvailablePackages = (state: RootState) => getPackages(state)?.availablePackages || [];

export const getPackagesToShow = (state: RootState) =>
    _filter(getAvailablePackages(state), (item) =>
        ["5g", "5g-ready", "4g", "5g-cabin", "5g-ready-cabin"].includes(item.type),
    );
export const getTVPackagesToShow = (state: RootState) =>
    _filter(getAvailablePackages(state), (item) => ["5g-tv", "5g-ready-tv", "5g-cabin-tv"].includes(item.type));
export const isNoPackages = (state: RootState) => !!getPackages(state)?.isNoPackages;

// Cart
export const getFwaCart = (state: RootState) => getFwaData(state).cart || {};
export const successfullyAddedProductToCart = (state: RootState) => getFwaCart(state).addToCartSuccess || false;
export const getSelectedPackageId = (state: RootState) => getFwaCart(state).packageId;

export const getSelectedContentfulPackageId = (state: RootState) => getFwaCart(state).contentfulPackageId;
export const getPackageFromId = (state: RootState, id?: number) =>
    _find(getAvailablePackages(state), (pck) => pck.id === id);
export const getSelectedPackageDisclaimerText = (state: RootState, id?: number) => {
    const selectedPackage = _find(getAvailablePackages(state), (pck) => pck.id === id);
    if (!selectedPackage) {
        return null;
    }

    return {
        offerDuration: 12,
        monthlyPrice: {
            discountPrice: selectedPackage.monthlyPrice.discountPrice,
        },
    };
};

export const getTitleForSelectedPackage = (state: RootState, id?: number) => {
    const selectedPackage = getPackageFromId(state, id);

    if (!selectedPackage) {
        return "Trådløst Bredbånd";
    }

    let subscriptionTitle = `${selectedPackage.name || "Trådløst Bredbånd"} ${selectedPackage.shortName || ""}`;
    // We dont have these in CF so populate name using linked variants names
    const isDiyPackage = diyPackageTypes.includes(selectedPackage.type as DiyPackageTypes);
    let linkedPackage: Package | undefined;
    if (isDiyPackage) {
        linkedPackage = getPackageFromId(state, selectedPackage.linkedPackageId);
        if (linkedPackage) {
            subscriptionTitle = `${linkedPackage.name || "Trådløst Bredbånd"} ${linkedPackage.shortName || ""}`;
        }
    }

    return subscriptionTitle;
};

// Speed are on the format "Bredbånd 25" or "Bredbånd 25 hytte". This function extracts the number using regex.
// All cases can be seen here: https://regex101.com/r/PGYLXI/1
export const getSpeedForSelectedPackage = (state: RootState) => {
    const id = getSelectedPackageId(state);
    const selectedPackage = _find(getAvailablePackages(state), (pck) => pck.id === id);
    if (!selectedPackage) {
        return null;
    }

    const speedRegex = /\b\d+\b/;
    const match = selectedPackage.configuration.speed.match(speedRegex);
    if (match) {
        return parseInt(match[0], 10);
    }

    return null;
};

export const getProductsForSelectedPackageId = (
    state: RootState,
    id?: number,
): {
    monthlyItems: Item[];
    totalDiscount: number;
    totalMonthlyBBDiscountedPrice?: number;
    totalMonthlyBBPrice?: number;
    totalUpfrontBBDiscountedPrice?: number;
    totalUpfrontBBPrice?: number;
    upfrontItems: Item[];
} => {
    const selectedPackage = _find(getAvailablePackages(state), (pck) => pck.id === id);
    if (!selectedPackage) {
        return {
            monthlyItems: [],
            upfrontItems: [],
            totalDiscount: 0,
        };
    }

    const subscriptionTitle = getTitleForSelectedPackage(state, id);
    const teliaPlayBoxProductId = 2077;
    const monthlyItems: Item[] = [];
    const upfrontItems: Item[] = [];

    let totalMonthlyBBPrice = 0;
    let totalMonthlyBBDiscountedPrice = 0;
    let totalUpfrontBBPrice = 0;
    let totalUpfrontBBDiscountedPrice = 0;
    let totalDiscount = 0;

    // Compute 1 line for BB (+ TV)
    _forEach(selectedPackage.products, (product) => {
        if (
            (product.monthlyPrice.regularPrice || product.monthlyPrice.discountPrice) &&
            !SMART_WIFI_ROUTER_PRODUCT_IDS.includes(product.id) &&
            product.id !== teliaPlayBoxProductId
        ) {
            totalMonthlyBBPrice += product.monthlyPrice.regularPrice;
            totalMonthlyBBDiscountedPrice += product.monthlyPrice.discountPrice;
        }
    });

    if (totalMonthlyBBDiscountedPrice !== totalMonthlyBBPrice) {
        monthlyItems.push({
            key: subscriptionTitle,
            value: `${getFormattedPrice(totalMonthlyBBDiscountedPrice)}/md.`,
            strikethrough: `${getFormattedPrice(totalMonthlyBBPrice)}/md.`,
        });
        totalDiscount += Math.max(totalMonthlyBBPrice - totalMonthlyBBDiscountedPrice, 0) * 6;
    } else {
        monthlyItems.push({
            key: subscriptionTitle,
            value: `${getFormattedPrice(totalMonthlyBBDiscountedPrice)}/md.`,
        });
    }

    // Compute 1 line for Smart Wifi
    const smartWifiProduct = _find(selectedPackage.products, (product) =>
        SMART_WIFI_ROUTER_PRODUCT_IDS.includes(product.id),
    );
    if (smartWifiProduct) {
        if (smartWifiProduct.monthlyPrice.regularPrice > smartWifiProduct.monthlyPrice.discountPrice) {
            monthlyItems.push({
                key: smartWifiProduct.name,
                value: `${getFormattedPrice(smartWifiProduct.monthlyPrice.discountPrice)}/md.`,
                strikethrough: `${getFormattedPrice(smartWifiProduct.monthlyPrice.regularPrice)}/md.`,
            });
        } else {
            monthlyItems.push({
                key: smartWifiProduct.name,
                value: `${getFormattedPrice(smartWifiProduct.monthlyPrice.regularPrice)}/md.`,
            });
        }
        totalMonthlyBBPrice += smartWifiProduct.monthlyPrice.regularPrice;
        totalMonthlyBBDiscountedPrice += smartWifiProduct.monthlyPrice.discountPrice;
        totalDiscount +=
            Math.max(smartWifiProduct.monthlyPrice.regularPrice - smartWifiProduct.monthlyPrice.discountPrice, 0) * 12;
    }

    // Compute 1 line for Telia Play Box
    const teliaPlayBoxProduct = _find(selectedPackage.products, (product) => product.id === teliaPlayBoxProductId);
    if (teliaPlayBoxProduct) {
        if (teliaPlayBoxProduct.monthlyPrice.regularPrice > teliaPlayBoxProduct.monthlyPrice.discountPrice) {
            monthlyItems.push({
                key: teliaPlayBoxProduct.name,
                value: `${getFormattedPrice(teliaPlayBoxProduct.monthlyPrice.discountPrice)}/md.`,
                strikethrough: `${getFormattedPrice(teliaPlayBoxProduct.monthlyPrice.regularPrice)}/md.`,
            });
        } else {
            monthlyItems.push({
                key: teliaPlayBoxProduct.name,
                value: `${getFormattedPrice(teliaPlayBoxProduct.monthlyPrice.regularPrice)}/md.`,
            });
        }
        totalMonthlyBBPrice += teliaPlayBoxProduct.monthlyPrice.regularPrice;
        totalMonthlyBBDiscountedPrice += teliaPlayBoxProduct.monthlyPrice.discountPrice;
        totalDiscount +=
            Math.max(
                teliaPlayBoxProduct.monthlyPrice.regularPrice - teliaPlayBoxProduct.monthlyPrice.discountPrice,
                0,
            ) * 12;
    }

    // Add on 1 line per upfront that differ from 0
    _forEach(selectedPackage.products, (product) => {
        if (product.installationPrice.regularPrice > 0 || product.installationPrice.discountPrice > 0) {
            const productName = product.name.toLocaleLowerCase().includes("installasjonskostnad")
                ? product.name
                : `Installasjonskostnad ${product.name}`;
            if (product.installationPrice.regularPrice === product.installationPrice.discountPrice) {
                upfrontItems.push({
                    key: productName,
                    value: getFormattedPrice(product.installationPrice.discountPrice),
                });
            } else {
                upfrontItems.push({
                    key: productName,
                    value: getFormattedPrice(product.installationPrice.discountPrice),
                    strikethrough: getFormattedPrice(product.installationPrice.regularPrice),
                });
            }
            totalUpfrontBBPrice += product.installationPrice.regularPrice;
            totalUpfrontBBDiscountedPrice += product.installationPrice.discountPrice;
        }
    });

    return {
        monthlyItems,
        upfrontItems,
        totalDiscount,
        totalMonthlyBBPrice,
        totalMonthlyBBDiscountedPrice,
        totalUpfrontBBPrice,
        totalUpfrontBBDiscountedPrice,
    };
};
export const isSelectedPackageCabinDIY = (state: RootState) => {
    const id = getSelectedPackageId(state);
    const selectedPackage = _find(getAvailablePackages(state), (pck) => pck.id === id);
    if (!selectedPackage) {
        return false;
    }

    return ["5g-ready-cabin-diy", "5g-cabin-diy", "5g-cabin-tv-diy"].includes(selectedPackage.type);
};

export const isSelectedPackageCabin = (state: RootState) => {
    const id = getSelectedPackageId(state);
    const selectedPackage = _find(getAvailablePackages(state), (pck) => pck.id === id);
    if (!selectedPackage) {
        return false;
    }

    return selectedPackage.type.includes("cabin");
};

export const isSelectedPackageDIY = (state: RootState) => {
    const id = getSelectedPackageId(state);
    const selectedPackage = _find(getAvailablePackages(state), (pck) => pck.id === id);
    if (!selectedPackage) {
        return false;
    }

    return diyPackageTypes.includes(selectedPackage.type as DiyPackageTypes);
};

// Content
export const getFwaContent = (state: RootState) => getFwaData(state).content;
export const getFwaContentBlocks = (state: RootState) => getFwaContent(state)?.blocks || [];

// Personal details
export const getFwaPersonalDetails = (state: RootState) => getFwaData(state).personalDetails;
export const successfullySubmittedOrder = (state: RootState) => !!getFwaPersonalDetails(state)?.submitOrderSuccess;
export const hasRequiredPersonalDetailsValues = (state: RootState): boolean => {
    const form = getFormById(state, FORM_ID.PERSONAL_DETAILS_FWA);
    const details = _get(form, "values.details", {}) as Record<string, string>;
    const hasEmail = !!details[VALIDATION_FIELD.PRODUCT_USER_EMAIL];
    const hasPhone = !!details[VALIDATION_FIELD.PRODUCT_USER_PHONE];
    if (!hasEmail || !hasPhone) return false;
    if (!isSelectedPackageCabinDIY(state) || !isCustomAddressSelected(state)) return true;
    return (
        !!details[VALIDATION_FIELD.STREET] && !!details[VALIDATION_FIELD.POSTCODE] && !!details[VALIDATION_FIELD.CITY]
    );
};

// MISC
export const getDIYModal = (state: RootState) => {
    const diyModalId = "FWA_DIY_MODAL";
    const modals = _get(state, "modals", []);
    return _find(modals, (modal) => modal.forEntryId === diyModalId);
};
export const getDefaultTab = (state: RootState) => getPackages(state)?.defaultTab;
